class ProgressTracker {
    /**
     * @typedef {Object} ProgressTrackerOptions
     * @property {number} initSaveScheduleSeconds
     * @property {number} saveScheduleSeconds
     * 
     * @param {_Kpi} kpi 
     * @param {ProgressTrackerOptions} [options]
     */
    constructor(kpi, options) {
        this.kpi = kpi;

        this._options = options || {
            initSaveScheduleSeconds: 0,
            saveScheduleSeconds: 0,
        };

        /** @type {KpiProgress} */
        this.progress = this.progress;

        /** @type {Date | undefined} */
        this._startDate = undefined;

        /** @type {NodeJS.Timeout | undefined} */
        this._saveTimeout = undefined;

        this.reset();
    }

    /**
     * @param {Date} [startDate] - defaults to now
     */
    reset(startDate) {
        const previousProgress = this.progress;

        this.progress = {
            attempts: 0,
            errors: 0,
            time: 0,
            completed: false,
        };

        this.setStartDate();

        if (this._isSaveScheduled()) {
            this._cancelScheduledSave();
            if (previousProgress.attempts > 0)
                this._saveNow(previousProgress);
        }

        this._scheduleSave(this._options.initSaveScheduleSeconds);
    }

    /**
     * @param {Date} [date] - defaults to now
     */
    setStartDate(date) {
        this._startDate = date || new Date();
    }

    getElapsedSeconds() {
        if (this._startDate === undefined)
            return undefined;

        // @ts-ignore
        let ms = new Date() - this._startDate;
        return (ms / 1000);
    }

    /**
     * @param {boolean} [completed]
     */
    saveSuccessfulAttempt(completed) {
        this._saveAttempt(completed);
    }

    /**
     * @param {boolean} [completed]
     */
    saveFailedAttempt(completed) {
        this.progress.errors += 1;
        this._saveAttempt(completed);
    }
    
    /**
     * @param {ProgressTrackerOptions} options
     */
    setOptions(options) {
        if (!options)
            return;

        /** @type {ProgressTrackerOptions} */
        const safeOptions = {
            initSaveScheduleSeconds: options.initSaveScheduleSeconds ?? this._options.initSaveScheduleSeconds,
            saveScheduleSeconds: options.saveScheduleSeconds ?? this._options.saveScheduleSeconds,
        };

        const prevOptions = this._options;
        this._options = safeOptions;

        const areNewOptionsQuicker = safeOptions.initSaveScheduleSeconds < prevOptions.initSaveScheduleSeconds
            || safeOptions.saveScheduleSeconds < prevOptions.saveScheduleSeconds;

        if (areNewOptionsQuicker && this._isSaveScheduled()) {
            this._cancelScheduledSave();
            this._saveNow(this.progress);
        }
    }
    
    forceSaveNow() {
        this._cancelScheduledSave();
        this._saveNow(this.progress);
    }

    /**
     * @param {boolean} [completed]
     */
    _saveAttempt(completed) {
        const wasCompleted = this.progress.completed;

        if (completed === undefined)
            completed = this.progress.completed;

        this.progress.attempts += 1;
        this.progress.completed = completed;
        this.progress.time = this.getElapsedSeconds() || 0;

        if (!wasCompleted && completed) {
            this._cancelScheduledSave();
            this._saveNow(this.progress);
        }
        else {
            this._scheduleSave(this._options.saveScheduleSeconds);
        }
    }

    /**
     * @param {KpiProgress} progress 
     */
    _saveNow(progress) {
        return this.kpi.saveProgress(progress)
            .then(() => console.log("progress saved", progress))
            .catch(err => console.error(err));
    }

    _isSaveScheduled() {
        return this._saveTimeout !== undefined;
    }

    /**
     * @param {number} seconds 
     */
    _scheduleSave(seconds) {
        if (this._isSaveScheduled())
            return;

        this._saveTimeout = setTimeout(() => {
            this._saveNow(this.progress)
                .then(() => { this._saveTimeout = undefined; });
        }, seconds * 1000);
    }

    _cancelScheduledSave() {
        if (this._isSaveScheduled()) {
            // @ts-ignore
            clearTimeout(this._saveTimeout);
            this._saveTimeout = undefined;
        }
    }
}

export default ProgressTracker;